import { createGlobalState } from "@vueuse/core"
import { getDevStoreOverride, applyDevStoreBypassParam, handleDevStoreAuthFailure } from "~/utils/devStoreOverride"

export const useApi = createGlobalState(() => {
  const config = useRuntimeConfig()
  const storeId = ref('')

  const redirectToLogin = () => {
    if (!import.meta.client) return
    const isFileProtocol = window.location.protocol === 'file:'
    const isAlreadyAtLogin = isFileProtocol
      ? window.location.hash === '#/login'
      : window.location.pathname === '/login'
    if (isAlreadyAtLogin) return

    // localStorage.removeItem('token')
    // localStorage.removeItem('firestore_loaded')
    if (isFileProtocol) {
      window.location.hash = '#/login'
      return
    }
    window.location.href = '/login'
  }

  const setStoreId = (id: string) => {
    storeId.value = id
  }
  
  const createInstance = () => {
    return $fetch.create({
      baseURL: config.public.apiBase,
      headers: {
        'Content-Type': 'application/json'
      },
      onRequest({ request: _request, options }) {
        if (import.meta.client) {
          // Mobile owner pages use a separate token key so they don't collide
          // with the desktop POS session in localStorage.token.
          const isOwnerMobile = window.location.pathname.startsWith('/v2-owner-report-mobile')
          const token = localStorage.getItem(isOwnerMobile ? 'mobile_token' : 'token')
          if (token) {
            options.headers = {
              ...options.headers,
              Authorization: `Bearer ${token}`
            } as any
          }
        }

        if (!options.query) {
          options.query = {}
        }
        if (!options.query.customerId) {
          options.query.customerId = storeId.value
        }

        // DEV-ONLY: the store-override session (see auth.ts snapshotAuth) has no
        // token, so a SECURITY_ENABLED backend would 401/403. Apply the server's
        // scope-bypass query pair so auth + customerId-scope checks are skipped.
        // The secret lives in the dev's localStorage (never in source) and this
        // whole call is import.meta.dev-gated → stripped from prod bundles.
        if (import.meta.dev && getDevStoreOverride()) {
          applyDevStoreBypassParam(options.query as any)
        }
      },
      onResponse({ request: _request, response: _response, options: _options }) {
        // Example: handle success logging
      },
      onResponseError({ request: _request, response }) {
        if (!import.meta.client) return
        const isOwnerMobile = window.location.pathname.startsWith('/v2-owner-report-mobile')
        if (isOwnerMobile && response?.status === 401) {
          useOwnerSessionExpired().open()
          return
        }
        // Change-password uses 401 to signal "current password incorrect" — that
        // is a form validation error, NOT an expired session. Let it propagate to
        // the caller's catch instead of forcing a logout/redirect.
        const reqUrl = typeof _request === 'string' ? _request : (_request as Request)?.url ?? ''
        if (reqUrl.includes('/change-password')) return
        // Desktop POS: with 7-day TTL + 3-day renew threshold + visibilitychange
        // watcher + Firestore credHash listener, reaching here means the user
        // hasn't opened the app for 7+ days (token fully expired) or there's a
        // clock-skew edge case. Either way, redirect to /login.
        if (!isOwnerMobile && response?.status === 401) {
          // DEV store override: a 401 means the bypass pair is missing/wrong —
          // /login can't fix that and would loop back into the override.
          if (import.meta.dev && handleDevStoreAuthFailure(401, reqUrl)) return
          if (window.location.pathname !== '/login') {
            window.location.href = '/login'
          }
        }
      }
    })
  }

  const api = createInstance()

  return {
    $api: api,
    setStoreId,
    createInstance
  }
})
